import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CommonModalForm from '../../components/commonComponent/CommonModalForm';
import { validateForm } from '../../utils/formUtils';
import { toastSuccess, toastError, confirmDelete } from '../../utils/alertHelper';
import axios from '../../utils/axiosInstance';
import { FaEdit, FaTrash } from 'react-icons/fa';
import { hasPermission } from "../../utils/permissionHelper";

const CoreTeamPage = () => {
  const navigate = useNavigate();
  const [coreTeam, setCoreTeam] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formValues, setFormValues] = useState({
    contributionLevel: '',
    chapter: '',
    cluster: '',
    coreTeamMaster: '',
  });
  const [editingId, setEditingId] = useState(null);
  const [errors, setErrors] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [dropdowns, setDropdowns] = useState({ contributionLevels: [], chapters: [], clusters: [], coreTeamMasters: [] });

  const limit = parseInt(process.env.REACT_APP_PAGE_LIMIT || 10);
  // Fetch dropdowns for contribution levels, chapters, clusters, core team masters
  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        
        // Fetch contribution levels
        const contributionLevelRes = await axios.get('/admin/api/contribution-levels');
        
        // Fetch core team masters
        const coreTeamMasterRes = await axios.get('/admin/api/core-teams');
        
        // Fetch chapters and clusters
        const [chapterRes, clusterRes] = await Promise.all([
          axios.get('/admin/api/clusters/all'),
          axios.get('/admin/api/chapters/all'),
        ]);
         
        // Process contribution levels data
        const contributionLevelsData = contributionLevelRes.data?.contributionLevels || contributionLevelRes.data?.data || contributionLevelRes.data || [];
        
        // Process core team masters data  
        const coreTeamMastersData = coreTeamMasterRes.data?.coreTeams || coreTeamMasterRes.data?.data || coreTeamMasterRes.data || [];
        
        // Process other data
        const chaptersData = chapterRes.data?.chapters || chapterRes.data?.data || chapterRes.data || [];
        const clustersData = clusterRes.data?.clusters || clusterRes.data?.data || clusterRes.data || [];
        
        const processedDropdowns = {
          contributionLevels: Array.isArray(contributionLevelsData) ? contributionLevelsData.map(cl => ({ 
            value: cl._id, 
            label: cl.name 
          })) : [],
          coreTeamMasters: Array.isArray(coreTeamMastersData) ? coreTeamMastersData.map(ctm => ({ 
            value: ctm._id, 
            label: ctm.name 
          })) : [],
          chapters: Array.isArray(chaptersData) ? chaptersData.map(c => ({ 
            value: c._id, 
            label: c.name, 
            cluster: c.cluster // This will be an array of cluster IDs
          })) : [],
          clusters: Array.isArray(clustersData) ? clustersData.map(c => ({ 
            value: c._id, 
            label: c.name 
          })) : [],
        };
        
        setDropdowns(processedDropdowns);
         
      } catch (err) {
        console.error('Failed to fetch dropdowns:', err);
        console.error('Error details:', err.response?.data);
        console.error('Error status:', err.response?.status);
        toastError('Failed to load dropdown data');
      }
    };
    fetchDropdowns();
  }, []);

  // Filter chapters based on selected cluster
  const filteredChapters = formValues.cluster
    ? dropdowns.chapters.filter(c => {
        // Handle both array and single cluster ID cases
        if (Array.isArray(c.cluster)) {
          return c.cluster.includes(formValues.cluster);
        }
        return c.cluster === formValues.cluster;
      })
    : dropdowns.chapters;

  // Get selected contribution level to determine conditional fields
  const selectedContributionLevel = dropdowns.contributionLevels.find(cl => cl.value === formValues.contributionLevel);
  const selectedContributionLevelName = selectedContributionLevel?.label?.toLowerCase() || '';

  const user = JSON.parse(localStorage.getItem("user")); // logged in user

  // Debounce search query - only trigger when 3+ characters or empty
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.length >= 3 || searchQuery.length === 0) {
        setDebouncedSearchQuery(searchQuery);
        setCurrentPage(1);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    if (hasPermission(user, "CoreTeam", "Read")) {
      fetchCoreTeam(currentPage, debouncedSearchQuery);
    }
  }, [currentPage, debouncedSearchQuery]);

  const fetchCoreTeam = async (page = 1, search = '') => {
    try {
      const res = await axios.get(`/admin/api/core-team/core?page=${page}&limit=${limit}&search=${search}`);
      const { coreTeams = [], totalCount = 0 } = res.data;

      setCoreTeam(coreTeams);
      setTotalPages(Math.ceil(totalCount / limit));
    } catch (err) {
      console.error('Failed to fetch Core Team:', err);
    }
  };

  const handleSave = async () => {
    
    // Dynamic required fields based on contribution level
    const requiredFieldsConfig = {
      contributionLevel: { required: true },
      coreTeamMaster: { required: true },
    };

    // Add conditional required fields based on contribution level
    if (selectedContributionLevelName.includes('cluster') && !selectedContributionLevelName.includes('chapter')) {
      requiredFieldsConfig.cluster = { required: true };
    } 
    if (selectedContributionLevelName.includes('chapter')) {
      requiredFieldsConfig.chapter = { required: true };
    }

    // Create a filtered form values object for validation (only include relevant fields)
    const formValuesForValidation = {
      contributionLevel: formValues.contributionLevel,
      coreTeamMaster: formValues.coreTeamMaster,
    };
    
    // Add conditional fields to validation if they are required
    if (requiredFieldsConfig.cluster) {
      formValuesForValidation.cluster = formValues.cluster;
    }
    if (requiredFieldsConfig.chapter) {
      formValuesForValidation.chapter = formValues.chapter;
    }


    const validationErrors = validateForm(formValuesForValidation, requiredFieldsConfig);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      // Prepare data to send, only include non-empty values
      const dataToSend = {};
      
      // Always include required fields
      if (formValues.contributionLevel) dataToSend.contributionLevel = formValues.contributionLevel;
      if (formValues.coreTeamMaster) dataToSend.coreTeamMaster = formValues.coreTeamMaster;
      
      // Include conditional fields based on contribution level
      if (selectedContributionLevelName.includes('cluster') && !selectedContributionLevelName.includes('chapter') && formValues.cluster) {
        dataToSend.cluster = formValues.cluster;
      }
      
      if (selectedContributionLevelName.includes('chapter') && formValues.chapter) {
        dataToSend.chapter = formValues.chapter;
      }
      
      if (editingId) {
        await axios.put(`/admin/api/core-team/${editingId}`, dataToSend);
        toastSuccess('Core Team updated successfully!');
      } else {
        await axios.post('/admin/api/core-team', dataToSend);
        toastSuccess('Core Team added successfully!');
      }
      setCurrentPage(1);
      fetchCoreTeam(1, searchQuery);
      handleCancel();
    } catch (err) {
      console.error('Failed to save Core Team:', err);
      console.error('Error response:', err.response?.data);
      toastError('Failed to save Core Team');
    }
  };

  const handleEdit = (coreTeam) => {
    setFormValues({
      contributionLevel: coreTeam.contributionLevel?._id || coreTeam.contributionLevel || '',
      chapter: coreTeam.chapter?._id || coreTeam.chapter || '',
      cluster: coreTeam.cluster?._id || coreTeam.cluster || '',
      coreTeamMaster: coreTeam.coreTeamMaster?._id || coreTeam.coreTeamMaster || '',
    });
    setEditingId(coreTeam._id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    const result = await confirmDelete('Are you sure you want to delete this Core Team member?');
    if (result?.isConfirmed) {
      try {
        await axios.delete(`/admin/api/core-team/${id}`);
        toastSuccess('Core Team deleted successfully!');
        setCurrentPage(1);
        fetchCoreTeam(1, searchQuery);
      } catch (err) {
        console.error('Failed to delete Core Team:', err);
        toastError('Failed to delete Core Team');
      }
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    setFormValues({
      contributionLevel: '',
      chapter: '',
      cluster: '',
      coreTeamMaster: '',
    });
    setEditingId(null);
    setErrors({});
  };

  // Handle form value changes with field clearing logic
  const handleFormChange = (newValues) => {
    
    // If contribution level changed, clear related fields
    if (newValues.contributionLevel !== formValues.contributionLevel) {
      newValues = {
        ...newValues,
        chapter: '',
        cluster: '',
      };
    }
    
    setFormValues(newValues);
  };
   if (!hasPermission(user, "CoreTeam", "Read")) {
    return (
      <div className="container py-4 text-center">
        <p className="text-danger fw-bold">
          🚫 You don’t have permission to view Core Team.
        </p>
      </div>
    );
  }

  return (
    <div className="container mt-2">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0"> Core Team</h3>
        <input
          type="text"
          className="form-control w-25"
          placeholder="Search Core Team (min 3 chars)..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
          }}
        />
      </div>

      <div className="card shadow-sm">
        <div className="card-header d-flex justify-content-between align-items-center p-2">
          <h5 className="mb-0">Core Team List</h5>
          {hasPermission(user, "CoreTeam", "Create") && (
          <button
            className="btn btn-sm btn-success"
            onClick={() => setShowModal(true)}
          >
            + Add Core Team
          </button>
        )}
        </div>
       <div className="table-responsive">
        <div className="card-body p-0">
          <table className="table table-bordered table-sm mb-0">
            <thead className="table-light">
              <tr>
                <th className="py-1 px-2 w-5">Sr no.</th>
                <th className="py-1 px-2">Contribution Level</th>
                <th className="py-1 px-2">Chapter</th>
                <th className="py-1 px-2">Core Team Master</th>
                <th className="py-1 px-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {coreTeam.length > 0 ? (
                coreTeam.map((member, idx) => {
                  const contributionLevelLabel = member.contributionLevel?.name || '';
                  const chapterLabel = member.chapter?.name || '';
                  const clusterLabel = member.cluster?.name || '';
                  const coreTeamMasterLabel = member.coreTeamMaster?.name || '';
                  return (
                    <tr key={member._id}>
                      <td className="py-1 px-2">{(currentPage - 1) * limit + idx + 1}</td>
                      <td className="py-1 px-2">{contributionLevelLabel}</td>
                      <td className="py-1 px-2">{chapterLabel}</td>
                      <td className="py-1 px-2">{coreTeamMasterLabel}</td>
                      <td className="py-1 px-2">
                       {hasPermission(user, "CoreTeam", "Edit") && (
                        <button
                          className="btn btn-sm me-1 py-0 px-2 fs-6 lh-sm shadow-none border-0 text-primary-custom"
        
                          title="Edit"
                          onClick={() => handleEdit(member)}
                        >
                          <FaEdit size={14} />
                        </button>
                      )}
                       {hasPermission(user, "CoreTeam", "Delete") && (
                        <button
                          className="btn btn-sm py-0 px-2 fs-6 lh-sm shadow-none border-0 text-danger"
                          onClick={() => handleDelete(member._id)}
                          title="Delete"
                        >
                          <FaTrash size={14} />
                        </button>
                      )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="6" className="text-center py-2">No Core Team found.</td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-end align-items-center gap-2 p-2">
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                &laquo;
              </button>

             {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
               <button
                  key={page}
                  className="{`btn btn-sm page-btn ${currentPage === page ? 'active' : ''}`}"
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              ))}

              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                &raquo;
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
      <CommonModalForm
        visible={showModal}
        title={editingId ? 'Edit Core Team' : 'Add Core Team'}
        fields={[
          { 
            name: 'contributionLevel', 
            label: 'Contribution Level', 
            type: 'select', 
            options: dropdowns.contributionLevels, 
            placeholder: 'Select Contribution Level' 
          },
          ...(formValues.contributionLevel ? [
            ...(selectedContributionLevelName.includes('cluster') && !selectedContributionLevelName.includes('chapter') ? [
              { name: 'cluster', label: 'Cluster', type: 'select', options: dropdowns.clusters, placeholder: 'Select Cluster' },
            ] : []),
            ...(selectedContributionLevelName.includes('chapter') ? [
              { name: 'chapter', label: 'Chapter', type: 'select', options: dropdowns.chapters, placeholder: 'Select Chapter' }
            ] : []),
          ] : []),
          { name: 'coreTeamMaster', label: 'Core Team Master', type: 'select', options: dropdowns.coreTeamMasters, placeholder: 'Select Core Team Master' },
        ]}
        values={formValues}
        errors={errors}
        onChange={handleFormChange}
        onSubmit={handleSave}
        onCancel={handleCancel}
        submitLabel={editingId ? 'Update' : 'Save'}
        requiredFields={[
          'contributionLevel', 
          'coreTeamMaster',
          ...(selectedContributionLevelName.includes('cluster') && !selectedContributionLevelName.includes('chapter') ? ['cluster'] : []),
          ...(selectedContributionLevelName.includes('chapter') ? ['chapter'] : []),
        ]}
      />
      
    </div>
  );
};

export default CoreTeamPage;
